import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@wds/grid';
import { Box } from '@wds/box';
import { Modal } from '@wds/modal';
import { Button } from '@wds/button';
import { IconArrowLeft, IconArrowRight } from '@wds/icons';
import { Link } from '@wds/link';
import AppDetailsFooter from './components/AppDetailsFooter';
import AppDetailsInfo from './components/AppDetailsInfo';
import AppDetailsFeatures from './components/AppDetailsFeatures';
import AppDetailsJumboHeader from './components/AppDetailsJumboHeader';
import useStyles from './styles';
import { useCarousel } from './components/CarouselContext';
import DisplayMedia from './components/DisplayMedia';

export const buildAppDetailsMediaFilesList = ({ details: { youtubeUrl = '', screenshots = [] }, name = '' }) => {
  const mediaList = [];

  if (youtubeUrl)
    mediaList.push({
      url: youtubeUrl,
      type: 'youtube',
      altText: name,
    });

  screenshots.forEach(s =>
    mediaList.push({
      url: s,
      type: 'image',
      altText: name,
    })
  );

  return mediaList;
};

const AppDetails = ({ appDetails, refreshDetails }) => {
  const {
    appDetailsHeader,
    contentContainer,
    appDetailsMediaModalImg,
    appDetailsMediaModalYoutube,
    appDetailsYoutubeFrame,
    appDetailsMediaModalYoutubeWrapper,
    appDetailsModalLeft,
    appDetailsModalMain,
    appDetailsModalRight,
  } = useStyles();

  const { mediaList, selectedMedia, selectMedia, updateMediaList, selectNextMedia, selectPreviousMedia } =
    useCarousel();

  const [showMedia, setShowMedia] = useState(false);

  const selectImageToShow = mediaIndex => {
    selectMedia(mediaIndex);
    setShowMedia(true);
  };

  useEffect(() => {
    updateMediaList(buildAppDetailsMediaFilesList(appDetails));
  }, [appDetails, updateMediaList]);

  return (
    <Box>
      <Box className={appDetailsHeader} pt={6} pb={7}>
        <Box mb={5}>
          <Link underline={false} href="/apps">
            <IconArrowLeft /> Back
          </Link>
        </Box>
        <Grid noBleeds>
          <Grid.Item lg={6} md={12} sm={12} xs={12}>
            <AppDetailsJumboHeader appDetails={appDetails} refreshDetails={refreshDetails} showCTA />
          </Grid.Item>
          <Grid.Item lg={6} md={12} sm={12} xs={12}>
            <If condition={mediaList && mediaList[0]}>
              <DisplayMedia media={mediaList[0]} maxHeight="325" onClick={() => selectImageToShow(0)} />
            </If>
          </Grid.Item>
        </Grid>
      </Box>

      <If condition={mediaList && mediaList.length > 1}>
        <Box className={contentContainer} pt={6} pb={5}>
          <Grid noBleeds>
            {mediaList.map(
              (media, index) =>
                index !== 0 &&
                index <= 5 && (
                  <Grid.Item sm={3} xs={6} key={media.url}>
                    <DisplayMedia media={media} onClick={() => selectImageToShow(index)} />
                  </Grid.Item>
                )
            )}
          </Grid>
        </Box>
      </If>
      <Box className={contentContainer} mb={8}>
        <AppDetailsFeatures featureList={appDetails.details.featureList} />
      </Box>
      <Box className={contentContainer} mb={5}>
        <AppDetailsInfo appDetails={appDetails} />
      </Box>
      <AppDetailsFooter appDetails={appDetails.details} />
      <If condition={mediaList.length > 0}>
        <Modal show={showMedia} size="xl" onClose={() => setShowMedia(false)} closeButtonLabel="Close Image">
          <Modal.Body>
            <Box display="grid" gridTemplateColumns="68px auto 68px">
              <Box className={appDetailsModalLeft}>
                <Button aria-label="Previous Screenshot" color="secondary" onClick={selectPreviousMedia} variant="text">
                  <IconArrowLeft size="xl" />
                </Button>
              </Box>
              <Box className={appDetailsModalMain} px={3}>
                <If condition={selectedMedia.type === 'youtube'}>
                  <Box className={appDetailsMediaModalYoutube}>
                    <Box className={appDetailsMediaModalYoutubeWrapper}>
                      <iframe
                        className={appDetailsYoutubeFrame}
                        data-testid="AppDetails__SelectedVideo"
                        allowFullScreen
                        src={selectedMedia.url}
                        title={selectedMedia.altText}
                      />
                    </Box>
                  </Box>
                </If>
                <If condition={selectedMedia.type !== 'youtube'}>
                  <Box display="grid" placeItems="center">
                    <img
                      alt={selectedMedia.altText}
                      data-testid="AppDetails__SelectedImage"
                      className={appDetailsMediaModalImg}
                      height="auto"
                      src={selectedMedia.url}
                    />
                  </Box>
                </If>
              </Box>
              <Box className={appDetailsModalRight}>
                <Button aria-label="Next Screenshot" color="secondary" onClick={selectNextMedia} variant="text">
                  <IconArrowRight size="xl" />
                </Button>
              </Box>
            </Box>
          </Modal.Body>
        </Modal>
      </If>
    </Box>
  );
};

AppDetails.propTypes = {
  appDetails: PropTypes.shape({
    id: PropTypes.string,
    isIntegration: PropTypes.bool,
    name: PropTypes.string,
    tagline: PropTypes.string,
    fullDescription: PropTypes.string,
    logo: PropTypes.string,
    installed: PropTypes.bool,
    details: PropTypes.shape({
      publisher: PropTypes.string,
      featureList: PropTypes.arrayOf(PropTypes.string),
      googleAnalyticsTrackingId: PropTypes.string,
      supportEmail: PropTypes.string,
      supportPhoneNumber: PropTypes.string,
      supportUrl: PropTypes.string,
      requirements: PropTypes.arrayOf(PropTypes.string),
      screenshots: PropTypes.arrayOf(PropTypes.string),
      privacyPolicyUrl: PropTypes.string,
      termsOfUseUrl: PropTypes.string,
      blogUrl: PropTypes.string,
      youtubeUrl: PropTypes.string,
      websiteUrl: PropTypes.string,
      installUrl: PropTypes.string,
      upgradeRequired: PropTypes.bool,
      optionalUpgrade: PropTypes.bool,
      scopes: PropTypes.arrayOf(PropTypes.string),
      link: PropTypes.string,
      linkType: PropTypes.string,
    }),
  }).isRequired,
  refreshDetails: PropTypes.func.isRequired,
};

export default AppDetails;
